import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, Search, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, getErrorMessage } from "@/lib/api";

/**
 * M03: staff-created application. Links an existing Candidate Master record to an
 * existing Job. If an ACTIVE application already exists for the same candidate+job,
 * the backend returns it (409) and we open that application instead of duplicating.
 */
export const NewApplicationDialog = ({ open, onOpenChange, isAdmin, onCreated }) => {
  const navigate = useNavigate();
  const basePath = isAdmin ? "/admin/applications" : "/recruiter/applications";

  const [candQ, setCandQ] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [candidate, setCandidate] = useState(null);
  const [priorities, setPriorities] = useState([]);
  const [priority, setPriority] = useState("");
  const [recruiters, setRecruiters] = useState([]);
  const [recruiterId, setRecruiterId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/master-data").then((res) => setPriorities(res.data.application_priority || [])).catch(() => {});
    if (isAdmin) {
      api.get("/users/recruiters").then((res) => setRecruiters(res.data.filter((r) => r.active))).catch(() => {});
    }
  }, [open, isAdmin]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api.get("/candidates", { params: { q: candQ || undefined, limit: 8 } })
        .then((res) => setCandidates(res.data.items || []))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [candQ, open]);

  const reset = () => {
    setCandQ(""); setCandidate(null); setPriority(""); setRecruiterId(""); setRemarks("");
  };

  const submit = async () => {
    if (!candidate) return toast.error("Choose a candidate from the Candidate Master");
    setBusy(true);
    try {
      const payload = { candidate_id: candidate.id };
      if (priority) payload.priority = priority;
      if (remarks.trim()) payload.remarks = remarks.trim();
      if (isAdmin && recruiterId && recruiterId !== "unassigned") payload.assigned_recruiter_id = recruiterId;
      const res = await api.post("/applications", payload);
      toast.success(`Application ${res.data.app_code} created`);
      onOpenChange(false);
      reset();
      onCreated?.();
      navigate(`${basePath}/${res.data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.existing_id) {
        toast.info(detail.message || "An active application already exists — opening it.");
        onOpenChange(false);
        reset();
        navigate(`${basePath}/${detail.existing_id}`);
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-lg rounded-xl" data-testid="new-application-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">New application</DialogTitle>
          <DialogDescription>
            Link an existing candidate to start a fresh application. The job is assigned automatically — one active application per candidate; duplicates open the existing journey.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Candidate (from Candidate Master) <span className="text-destructive">*</span></Label>
            {candidate ? (
              <div className="flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2" data-testid="selected-candidate">
                <UserRound className="h-4 w-4 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{candidate.full_name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{candidate.code} · {candidate.mobile}</p>
                </div>
                <Button variant="ghost" size="sm" className="h-7 rounded-lg" onClick={() => setCandidate(null)} data-testid="change-candidate-button">Change</Button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input data-testid="candidate-search-input" className="pl-9" placeholder="Search by name, mobile or UTC code..." value={candQ} onChange={(e) => setCandQ(e.target.value)} />
                </div>
                <div className="max-h-44 overflow-y-auto rounded-lg border border-border divide-y divide-border" data-testid="candidate-results">
                  {candidates.map((c) => (
                    <button key={c.id} type="button" onClick={() => setCandidate(c)} data-testid={`candidate-option-${c.id}`}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/60 transition-colors">
                      <UserRound className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{c.full_name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{c.code} · {c.mobile}</p>
                      </div>
                      <Check className="h-4 w-4 opacity-0" />
                    </button>
                  ))}
                  {candidates.length === 0 && <p className="px-3 py-3 text-sm text-muted-foreground">No candidates found.</p>}
                </div>
              </>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="priority-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {priorities.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div className="space-y-1.5">
                <Label>Assign to recruiter</Label>
                <Select value={recruiterId} onValueChange={setRecruiterId}>
                  <SelectTrigger data-testid="assign-recruiter-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {recruiters.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Remarks</Label>
            <Textarea data-testid="new-app-remarks-input" rows={2} placeholder="Optional context for this application..." value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </div>

          <Button onClick={submit} disabled={busy} className="w-full rounded-lg" data-testid="new-application-submit">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create application
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
